// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/query-client";
import { useToast } from "@/hooks/use-toast";
import { isUnauthorizedError } from "@/lib/auth-utils";
import { PERIL_TYPES } from "@/lib/enum-utils";
import { Sparkles, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { QuickAssignCard } from "@/components/claims/quick-assign-card";
import { CarrierCombobox } from "@/components/carriers/carrier-combobox";
import { computeArrayCommissionPercentage, MAX_COMMISSION_PERCENTAGE } from "@/lib/commission-validation";

const editClaimSchema = z.object({
  claimNumber: z.string().optional(),
  policyNumber: z.string().optional(),

  carrierId: z.string().nullable().optional(),

  claimType: z.enum(["residential", "commercial"]).default("residential"),
  policyholderFirstName: z.string().optional(),
  policyholderLastName: z.string().optional(),
  policyholderCompany: z.string().optional(),
  pointOfContact: z.string().optional(),
  policyholderEmail: z.string().email("Invalid email address").optional().or(z.literal("")),
  policyholderPhone: z.string().optional(),
  policyholderNotes: z.string().optional(),
  
  policyholder2FirstName: z.string().optional(),
  policyholder2LastName: z.string().optional(),
  policyholder2Email: z.string().email("Invalid email address").optional().or(z.literal("")),
  policyholder2Phone: z.string().optional(),
  

  
  dateOfLoss: z.string().min(1, "Date of loss is required"),
  perilType: z.enum(["fire", "water", "wind", "hail", "wind_hail", "tornado", "hurricane", "lightning", "smoke", "flood", "earthquake", "theft", "vandalism", "other"], {
    required_error: "Please select a peril type",
  }),
  description: z.string().optional(),
  
  propertyAddress: z.string().min(1, "Property address is required"),
  propertyCity: z.string().min(1, "City is required"),
  propertyState: z.string().min(1, "State is required"),
  propertyZip: z.string().min(1, "ZIP code is required"),
  propertyType: z.enum(["residential", "commercial", "industrial"]).optional(),
  yearBuilt: z.string().optional(),
  lenderName: z.string().optional(),
  mortgageLoanNumber: z.string().optional(),

  claimFiledDate: z.string().optional(),

  carrierPaperworkReceived: z.boolean().default(true),
  paperworkSource: z.enum(["email", "fax", "portal", "mail"]).optional(),
  dateOfLossVerified: z.boolean().default(false),
  fileType: z.enum(["public_adjuster", "appraisal", "estimate", "umpire", "other"]).optional(),
  

  
  estimatedValue: z.string().optional(),
  settlementValue: z.string().optional(),
  paAgreementCompanyFeeType: z.enum(["percent", "flat_rate", "hourly"]).optional(),
  paAgreementCompanyFeePercentage: z.string().optional(),
  paAgreementCompanyFlatAmount: z.string().optional(),
  paAgreementCompanyHourlyRate: z.string().optional(),
  feeBasedOnNetIncrease: z.boolean().default(false),
  mplcEngagementDate: z.string().optional(),
}).superRefine((data, ctx) => {
  // A claim (commercial OR residential) can be insured by a business or by an
  // individual, so require a business/company name OR the insured's first +
  // last name (GitHub #399).
  const hasCompany = !!data.policyholderCompany?.trim();
  const hasName = !!data.policyholderFirstName?.trim() && !!data.policyholderLastName?.trim();
  if (!hasCompany && !hasName) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [data.claimType === 'commercial' ? 'policyholderCompany' : 'policyholderFirstName'],
      message: "Enter a business/company name or the insured's first and last name",
    });
  }
});

/** Maps a claim field name to the data-testid of its input in the edit modal. */
const FIELD_TO_TESTID: Record<string, string> = {
  propertyAddress: 'input-property-address',
  propertyCity: 'input-property-city',
  propertyState: 'input-property-state',
  propertyZip: 'input-property-zip',
  policyNumber: 'input-policy-number',
  claimNumber: 'input-claim-number',
  dateOfLoss: 'input-date-of-loss',
  policyholderFirstName: 'input-policyholder-first-name',
  policyholderLastName: 'input-policyholder-last-name',
  policyholderEmail: 'input-policyholder-email',
  policyholder2FirstName: 'input-policyholder2-first-name',
  policyholder2LastName: 'input-policyholder2-last-name',
  policyholder2Email: 'input-policyholder2-email',
  paAgreementCompanyFeePercentage: 'input-company-fee-percentage',
  carrierId: 'input-carrier-id',
};

interface EditClaimModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claimId: string;
  claim: any;
  initialFocusField?: string;
  onSaved?: () => void;
}

export default function EditClaimModal({ open, onOpenChange, claimId, claim, initialFocusField, onSaved }: EditClaimModalProps) {
  const { toast } = useToast();

  // PA assignments state for the modal form
  const [paAssignmentsState, setPaAssignmentsState] = useState<any[]>([]);
  
  const { data: allAdjusters = [] } = useQuery<any[]>({
    queryKey: ['/api/users/adjusters'],
    enabled: open,
  });
  
  // Fetch PA assignments for this claim
  const { data: paAssignments = [] } = useQuery<any[]>({
    queryKey: [`/api/claims/${claimId}/pa-assignments`],
    enabled: open && !!claimId,
  });
  
  // Helper to find PA assignment by role
  const getPaByRole = (role: string) => {
    const assignment = paAssignments.find((a: any) => a.role === role && a.isActive);
    return assignment?.userId || "";
  };
  
  const form = useForm<EditClaimFormData>({
    resolver: zodResolver(editClaimSchema),
    defaultValues: {
      claimNumber: "",
      policyNumber: "",

      carrierId: null,
      claimType: "residential",
      policyholderFirstName: "",
      policyholderLastName: "",
      policyholderCompany: "",
      pointOfContact: "",
      policyholderEmail: "",
      policyholderPhone: "",
      policyholderNotes: "",
      policyholder2FirstName: "",
      policyholder2LastName: "",
      policyholder2Email: "",
      policyholder2Phone: "",

      dateOfLoss: "",
      perilType: "fire",
      description: "",
      propertyAddress: "",
      propertyCity: "",
      propertyState: "",
      propertyZip: "",
      propertyType: "residential",
      yearBuilt: "",
      lenderName: "",
      mortgageLoanNumber: "",
      claimFiledDate: "",
      carrierPaperworkReceived: true,
      paperworkSource: undefined,
      dateOfLossVerified: false,
      fileType: undefined,
      estimatedValue: "",
      settlementValue: "",
      paAgreementCompanyFeeType: "percent" as const,
      paAgreementCompanyFeePercentage: "",
      paAgreementCompanyFlatAmount: "",
      paAgreementCompanyHourlyRate: "",
      feeBasedOnNetIncrease: false,
      mplcEngagementDate: "",
    },
  });

  const companyFeeType = form.watch("paAgreementCompanyFeeType");

  useEffect(() => {
    if (claim && open) {
      const formatDateForInput = (dateString: string | null | undefined) => {
        if (!dateString) return "";
        // PG date columns return "YYYY-MM-DD" strings — use directly
        return String(dateString).substring(0, 10);
      };
      
      form.reset({
        claimNumber: claim.claimNumber || "",
        policyNumber: claim.policyNumber || "",

        carrierId: claim.carrierId || null,
        claimType: claim.claimType || "residential",
        policyholderFirstName: claim.policyholderFirstName || "",
        policyholderLastName: claim.policyholderLastName || "",
        policyholderCompany: claim.policyholderCompany || "",
        pointOfContact: claim.pointOfContact || "",
        policyholderEmail: claim.policyholderEmail || "",
        policyholderPhone: claim.policyholderPhone || "",
        policyholderNotes: claim.policyholderNotes || "",
        policyholder2FirstName: claim.policyholder2FirstName || "",
        policyholder2LastName: claim.policyholder2LastName || "",
        policyholder2Email: claim.policyholder2Email || "",
        policyholder2Phone: claim.policyholder2Phone || "",

        dateOfLoss: formatDateForInput(claim.dateOfLoss),
        perilType: claim.perilType || "fire",
        description: claim.description || "",
        propertyAddress: claim.propertyAddress || "",
        propertyCity: claim.propertyCity || "",
        propertyState: claim.propertyState || "",
        propertyZip: claim.propertyZip || "",
        propertyType: claim.propertyType || "residential",
        yearBuilt: claim.yearBuilt ? String(claim.yearBuilt) : "",
        lenderName: claim.lenderName || "",
        mortgageLoanNumber: claim.mortgageLoanNumber || "",
        claimFiledDate: formatDateForInput(claim.claimFiledDate),
        carrierPaperworkReceived: claim.carrierPaperworkReceived ?? true,
        paperworkSource: claim.paperworkSource || undefined,
        dateOfLossVerified: claim.dateOfLossVerified || false,
        fileType: claim.fileType || undefined,
        estimatedValue: claim.estimatedValue || "",
        settlementValue: claim.settlementValue || "",
        paAgreementCompanyFeeType: (claim.paAgreementCompanyFeeType ?? "percent") as "percent" | "flat_rate" | "hourly",
        paAgreementCompanyFeePercentage: claim.paAgreementCompanyFeePercentage || "",
        paAgreementCompanyFlatAmount: claim.paAgreementCompanyFlatAmount || "",
        paAgreementCompanyHourlyRate: claim.paAgreementCompanyHourlyRate || "",
        feeBasedOnNetIncrease: claim.feeBasedOnNetIncrease || false,
        mplcEngagementDate: formatDateForInput(claim.mplcEngagementDate),
      });

      // Initialize local assignments state from existing PA assignments
      const initialAssignments = paAssignments
        .filter((a: any) => a.isActive && a.userId)
        .map((a: any) => ({
          ...a,
          id: a.id || `form-${Date.now()}-${Math.random()}`
        }));
      setPaAssignmentsState(initialAssignments);
    }
  }, [claim, open, form, paAssignments]);

  // Scroll to and focus the target field when the modal opens with an initialFocusField
  useEffect(() => {
    if (!open || !initialFocusField) return;
    const testId = FIELD_TO_TESTID[initialFocusField];
    if (!testId) return;
    // Delay to allow the dialog animation and DOM paint to complete
    const timer = setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open, initialFocusField]);

  const updateClaimMutation = useMutation({
    mutationFn: async (data: EditClaimFormData) => {
      const response = await apiRequest("PATCH", `/api/claims/${claimId}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/claims"] });
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/accounting-summary`] });
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/pa-assignments`] });
      toast({
        title: "Claim Updated",
        description: "The claim has been updated successfully.",
      });
      onSaved?.();
      onOpenChange(false);
    },
    onError: async (error: any) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      // Try to extract the server error message
      let errorMessage = "Failed to update claim. Please try again.";
      try {
        if (error?.message) {
          errorMessage = error.message;
        }
      } catch {}
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Generate loss description from estimate line items
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  
  const generateLossDescription = async () => {
    setIsGeneratingDescription(true);
    try {
      const response = await fetch(`/api/claims/${claimId}/generate-loss-description`, {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast({
          title: "Cannot Generate",
          description: data.message || 'No estimates found for this claim.',
          variant: "destructive",
        });
        return;
      }
      
      if (data.description) {
        form.setValue('description', data.description);
        toast({
          title: "Description Generated",
          description: `Generated from ${data.estimateCount} estimate(s)`,
        });
      }
    } catch (error) {
      console.error('Error generating loss description:', error);
      toast({
        title: "Error",
        description: "Failed to generate description from estimates.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const onSubmit = (data: EditClaimFormData) => {
    // Validate commission percentages don't exceed 100% (only for percent-type roles)
    const totalPct = computeArrayCommissionPercentage(paAssignmentsState);
    
    if (totalPct > MAX_COMMISSION_PERCENTAGE) {
      toast({
        title: "Invalid Commission Percentages",
        description: `Total commission is ${totalPct.toFixed(1)}% — cannot exceed ${MAX_COMMISSION_PERCENTAGE}%.`,
        variant: "destructive",
      });
      return;
    }

    console.log('[EditClaimModal] Form submitted with data:', data);
    const cleanedData: any = {
      ...data,
      claimFiledDate: data.claimFiledDate || undefined,
      dateOfLoss: data.dateOfLoss || undefined,
      estimatedValue: data.estimatedValue || undefined,
      settlementValue: data.settlementValue || undefined,
      paAgreementCompanyFeePercentage: data.paAgreementCompanyFeePercentage || undefined,
      paAgreementCompanyFlatAmount: data.paAgreementCompanyFlatAmount || undefined,
      paAgreementCompanyHourlyRate: data.paAgreementCompanyHourlyRate || undefined,
      yearBuilt: data.yearBuilt ? parseInt(data.yearBuilt) : undefined,
    };

    // Always include paAssignments so the backend can update commission data
    cleanedData.paAssignments = paAssignmentsState
      .filter((a: any) => a.userId && a.userId !== "none")
      .map((a: any) => {
        const copy = { ...a };
        // Don't send temporary React IDs to backend
        if (String(copy.id).startsWith("form-")) {
          delete copy.id;
        }
        return copy;
      });

    console.log('[EditClaimModal] Sending cleanedData:', cleanedData);
    updateClaimMutation.mutate(cleanedData);
  };
  
  const handleFormError = (errors: any) => {
    console.error('[EditClaimModal] Form validation errors:', errors);
    const errorFields = Object.keys(errors);
    const firstError = errors[errorFields[0]]?.message || 'Please check all required fields';
    toast({
      title: "Validation Error",
      description: `${firstError} (${errorFields.length} field${errorFields.length > 1 ? 's' : ''} with issues: ${errorFields.slice(0, 3).join(', ')}${errorFields.length > 3 ? '...' : ''})`,
      variant: "destructive",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground">
            Edit Claim: {claim?.claimNumber}
          </DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, handleFormError)} className="space-y-6">
            {/* Carrier Paperwork Information */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-medium text-foreground mb-4">📋 Carrier Paperwork Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="claimNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Claim Number</FormLabel>
                      <FormControl>
                        <Input placeholder="CLM-2024-001" {...field} data-testid="input-claim-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Policy Number</FormLabel>
                      <FormControl>
                        <Input placeholder="POL-123456" {...field} data-testid="input-policy-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>

              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="carrierId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Insurance Carrier</FormLabel>
                      <FormControl>
                        <CarrierCombobox
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select or create carrier..."
                          data-testid="input-carrier-id"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <FormField
                  control={form.control}
                  name="paperworkSource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Paperwork Source</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-paperwork-source">
                            <SelectValue placeholder="Select source" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="fax">Fax</SelectItem>
                          <SelectItem value="portal">Portal</SelectItem>
                          <SelectItem value="mail">Mail</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fileType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Process Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-file-type">
                            <SelectValue placeholder="Select process type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="public_adjuster">Public Adjuster (PA)</SelectItem>
                          <SelectItem value="appraisal">Appraisal</SelectItem>
                          <SelectItem value="estimate">Estimate Only</SelectItem>
                          <SelectItem value="umpire">Umpire</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Policyholder Information */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="text-lg font-medium text-foreground mb-4">👤 Policyholder Information</h3>
              
              {/* Claim Type Selector */}
              <div className="mb-4">
                <FormField
                  control={form.control}
                  name="claimType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Claim Type <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select
                        onValueChange={(value) => {
                          // Keep any entered names/company when switching type —
                          // an individual can be the insured on a commercial
                          // claim, so clearing names would discard valid data
                          // (GitHub #399).
                          field.onChange(value);
                        }}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-claim-type">
                            <SelectValue placeholder="Select claim type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="residential">Residential (Individual)</SelectItem>
                          <SelectItem value="commercial">Commercial (Business)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Commercial: a business OR an individual can be the named
                    insured (GitHub #399), so show the company field AND the
                    person's name fields. Neither alone is hard-required — the
                    schema enforces "company OR first + last name". */}
                {form.watch('claimType') === 'commercial' ? (
                  <React.Fragment key="commercial-fields">
                    <FormField
                      control={form.control}
                      name="policyholderCompany"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Business/Company Name</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} placeholder="e.g., ALUMNUS GARDENS, LLC" data-testid="input-policyholder-company" />
                          </FormControl>
                          <p className="text-xs text-muted-foreground mt-0.5">Company or first + last name required</p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="policyholderFirstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} value={field.value ?? ""} data-testid="input-policyholder-first-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="policyholderLastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Doe" {...field} value={field.value ?? ""} data-testid="input-policyholder-last-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="pointOfContact"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Point of Contact</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} placeholder="Contact person's name" data-testid="input-point-of-contact" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </React.Fragment>
                ) : (
                  <React.Fragment key="residential-fields">
                    <FormField
                      control={form.control}
                      name="policyholderFirstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name <span className="text-destructive">*</span></FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} value={field.value ?? ""} data-testid="input-policyholder-first-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="policyholderLastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name <span className="text-destructive">*</span></FormLabel>
                          <FormControl>
                            <Input placeholder="Doe" {...field} value={field.value ?? ""} data-testid="input-policyholder-last-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </React.Fragment>
                )}

                <FormField
                  control={form.control}
                  name="policyholderEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john.doe@email.com" {...field} value={field.value ?? ""} data-testid="input-policyholder-email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyholderPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <PhoneInput {...field} value={field.value ?? ""} data-testid="input-policyholder-phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="policyholderNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Additional notes about policyholder..." {...field} data-testid="textarea-policyholder-notes" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-6 pt-4 border-t border-blue-200 dark:border-blue-700">
                <h4 className="text-md font-medium text-foreground mb-3">Second Policyholder (Optional)</h4>
                <p className="text-sm text-muted-foreground mb-4">If there is a joint policyholder, enter their information below. Documents will be sent to both policyholders for signature.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="policyholder2FirstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Jane" {...field} data-testid="input-policyholder2-first-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="policyholder2LastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} data-testid="input-policyholder2-last-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="policyholder2Email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="jane.doe@email.com" {...field} data-testid="input-policyholder2-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="policyholder2Phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <PhoneInput {...field} data-testid="input-policyholder2-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Property Information */}
            <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
              <h3 className="text-lg font-medium text-foreground mb-4">🏠 Property Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="propertyAddress"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Property Address <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="123 Main St" {...field} data-testid="input-property-address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyCity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="City" {...field} data-testid="input-property-city" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyState"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="CA" {...field} data-testid="input-property-state" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyZip"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP Code <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="90210" {...field} data-testid="input-property-zip" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Property Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-property-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="industrial">Industrial</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="yearBuilt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="YYYY"
                          min="1800"
                          max="2026"
                          {...field}
                          data-testid="input-year-built"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lenderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mortgage/LOC Lender Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Wells Fargo, Chase" {...field} data-testid="input-lender-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mortgageLoanNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mortgage/LOC Loan Number</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., 1234567890" {...field} data-testid="input-mortgage-loan-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Loss Information */}
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
              <h3 className="text-lg font-medium text-foreground mb-4">🔥 Loss Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="dateOfLoss"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of Loss <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input type="date" max={new Date().toISOString().split('T')[0]} {...field} data-testid="input-date-of-loss" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="perilType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Peril Type <span className="text-destructive">*</span></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-peril-type">
                            <SelectValue placeholder="Select peril type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PERIL_TYPES.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Description</FormLabel>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={generateLossDescription}
                          disabled={isGeneratingDescription}
                          className="h-7 text-xs"
                          data-testid="button-generate-description"
                        >
                          {isGeneratingDescription ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3 w-3 mr-1" />
                              Generate from Estimates
                            </>
                          )}
                        </Button>
                      </div>
                      <FormControl>
                        <Textarea placeholder="Describe the loss incident..." {...field} rows={3} data-testid="textarea-description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Claim Filed Date and Verification */}
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <h3 className="text-lg font-medium text-foreground mb-4">📅 Claim Filing Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="claimFiledDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Claim Filed Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} data-testid="input-claim-filed-date" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dateOfLossVerified"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0 pt-8">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-date-verified"
                        />
                      </FormControl>
                      <FormLabel className="font-normal">
                        Date of loss verified
                      </FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Public Adjuster Assignment */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-lg font-medium text-foreground mb-4">👥 Public Adjuster Assignment</h3>
              <QuickAssignCard
                mode="form"
                adjusters={allAdjusters}
                claimState={form.watch("propertyState")}
                bare
                layout="grid-cols-1 md:grid-cols-2"
                assignments={paAssignmentsState}
                onAssignmentsChange={setPaAssignmentsState}
                roles={[
                  { role: "sales_pa", label: "Sales Public Adjuster", testId: "select-sales-adjuster-edit", roleType: "sales_pa" },
                  { role: "field_pa", label: "Field Public Adjuster", testId: "select-field-adjuster-edit", roleType: "field_pa" },
                  { role: "desk_pa", label: "Desk Public Adjuster", testId: "select-desk-adjuster-edit", roleType: "desk_pa" },
                ]}
                initialDynamicRoles={
                  paAssignments
                    .filter((a: any) => a.isActive && !['sales_pa', 'field_pa', 'desk_pa'].includes(a.role))
                    .map((a: any) => {
                      const roleKey = a.role === 'other' && a.customRoleName ? a.customRoleName.replace(/\s+/g, '_') : a.role;
                      return {
                        role: roleKey,
                        label: a.customRoleName || a.role.replace(/_/g, ' '),
                        testId: `qa-select-${roleKey}`,
                        roleType: roleKey,
                      };
                    })
                }
              />
            </div>

            {/* Financial Information */}
            <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg border border-pink-200 dark:border-pink-800">
              <h3 className="text-lg font-medium text-foreground mb-4">💰 Financial Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="estimatedValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estimated PA Value</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="100000.00" {...field} data-testid="input-estimated-value-edit" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="settlementValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Settlement Offer Value</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="50000.00" {...field} data-testid="input-settlement-value-edit" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Fee type selector — occupies the third column of the 3-col grid */}
                <FormField
                  control={form.control}
                  name="paAgreementCompanyFeeType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Fee Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? "percent"}>
                        <FormControl>
                          <SelectTrigger data-testid="select-company-fee-type-edit">
                            <SelectValue placeholder="Select fee type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="percent">Percentage (%)</SelectItem>
                          <SelectItem value="flat_rate">Flat Amount ($)</SelectItem>
                          <SelectItem value="hourly">Hourly ($/hr)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Conditional fee amount fields — rendered outside the 3-col grid */}
              {(!companyFeeType || companyFeeType === "percent") && (
                <div className="mt-4">
                  <FormField
                    control={form.control}
                    name="paAgreementCompanyFeePercentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Fee % (PA Agreement)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="10.00" {...field} data-testid="input-company-fee-percentage" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {companyFeeType === "flat_rate" && (
                <div className="mt-4">
                  <FormField
                    control={form.control}
                    name="paAgreementCompanyFlatAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Flat Fee ($)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="500.00" {...field} data-testid="input-company-flat-amount-edit" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {companyFeeType === "hourly" && (
                <div className="mt-4">
                  <FormField
                    control={form.control}
                    name="paAgreementCompanyHourlyRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Hourly Rate ($/hr)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="150.00" {...field} data-testid="input-company-hourly-rate-edit" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Fee Basis Settings — existing section follows */}
              {/* Fee Basis Settings */}
              <div className="mt-4 pt-4 border-t border-pink-200 dark:border-pink-800">
                <h4 className="text-sm font-medium text-foreground mb-3">Fee Calculation Basis</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="feeBasedOnNetIncrease"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                        <div className="space-y-0.5">
                          <FormLabel>Fee Based on Net Increase</FormLabel>
                          <p className="text-xs text-muted-foreground">
                            Calculate fee on settlement increase above initial value
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-fee-net-increase"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="mplcEngagementDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>MPLC Engagement Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} data-testid="input-mplc-engagement-date" />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Date MPLC was retained (for net increase calculation)
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel-edit"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateClaimMutation.isPending}
                data-testid="button-save-claim"
              >
                {updateClaimMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
